import React, { useState, useEffect, useMemo } from 'react';
import StepHeader from './StepHeader';
import { ClientProposalRelevantInformation, SimiliarRole, WorkflowStep } from './useWorkflow';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  DefaultButton,
  TextField,
  Checkbox,
  IconButton,
  Spinner,
  Modal,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  Overlay,
} from '@fluentui/react';
import { Divider } from '@fluentui/react-components';
import { appConfig } from '../../../appConfig';
import { useAuthErrorContext } from '../../../components/AuthErrorProvider';
import { NewRoleCard } from '../../../components/NewRoleCard';
import { AddRegular } from '@fluentui/react-icons';
import styles from './Step3.module.scss';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { updateSimilarRole, fetchSimilarRoles, uploadClientProposalStep3 } from '../../../api/api';
import { FetchSimilarRolesRequest } from '../../../api/models';

interface Step3Props {
  step2Data?: {
    organisation: string;
    roleTitle: string;
    roleType: string;
    sector: string;
  };
  data: {
    organisation?: string;
    roleTitle?: string;
    roleType?: string;
    sector?: string;
    consultingTeam?: Array<{ id: string; name: string }>;
    filename?: string;
  } & Partial<ClientProposalRelevantInformation>;
  onComplete: (data: ClientProposalRelevantInformation) => void;
  onBack: () => void;
  onStepClick: (step: WorkflowStep) => void;
  onSubmit: (data: ClientProposalRelevantInformation) => Promise<void>;
  isLoading: boolean;
}

interface DraggableItemProps {
  item: SimiliarRole;
  index: number;
  columnId: keyof ClientProposalRelevantInformation;
  moveItem: (sourceIndex: number, destinationIndex: number, columnId: keyof ClientProposalRelevantInformation) => void;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: (item: SimiliarRole) => void;
}

const ItemType = 'CARD';

const DraggableItem: React.FC<DraggableItemProps> = ({
  item,
  index,
  columnId,
  moveItem,
  isSelected,
  onSelect,
  onEdit,
}) => {
  const [, ref] = useDrag({
    type: ItemType,
    item: { index, columnId },
  });

  const [, drop] = useDrop({
    accept: ItemType,
    hover: (draggedItem: { index: number; columnId: string }) => {
      if (draggedItem.index !== index || draggedItem.columnId !== columnId) {
        moveItem(draggedItem.index, index, columnId);
        draggedItem.index = index;
        draggedItem.columnId = columnId;
      }
    },
  });

  return (
    <div ref={node => ref(drop(node))} className={styles.draggableItem}>
      <div className={styles.itemContent}>
        <div style={{ marginTop: '0.3rem' }}>
          <Checkbox checked={isSelected} onChange={onSelect} />
        </div>
        <div>
          <strong>{item.role}</strong>
          <div>{item.organization}</div>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-center gap-2">
        <IconButton iconProps={{ iconName: 'Edit' }} onClick={() => onEdit(item)} />
        <div style={{ cursor: 'grab' }}>
          <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="2" cy="2" r="2" fill="#ccc" />
            <circle cx="2" cy="8" r="2" fill="#ccc" />
            <circle cx="2" cy="14" r="2" fill="#ccc" />
            <circle cx="2" cy="20" r="2" fill="#ccc" />
            <circle cx="8" cy="2" r="2" fill="#ccc" />
            <circle cx="8" cy="8" r="2" fill="#ccc" />
            <circle cx="8" cy="14" r="2" fill="#ccc" />
            <circle cx="8" cy="20" r="2" fill="#ccc" />
          </svg>
        </div>
      </div>
    </div>
  );
};

interface SearchResult {
  Job: string;
  Company: string;
  PartitionKey: string;
  RowKey: string;
}

interface EditModalData {
  id: string;
  rowKey: string;
  partitionKey: string;
  Company: string;
  Job: string;
}

const Step3: React.FC<Step3Props> = ({ data, step2Data, onComplete, onBack, onStepClick, onSubmit, isLoading }) => {
  const { handleAuthError } = useAuthErrorContext();
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [columns, setColumns] = useState<ClientProposalRelevantInformation>(() => {
    if (data?.similiarRoles || data?.similiarSectors || data?.similiarOrganisations) {
      return {
        similiarRoles: data.similiarRoles || [],
        similiarSectors: data.similiarSectors || [],
        similiarOrganisations: data.similiarOrganisations || [],
        similiarCustom: data.similiarCustom || undefined,
      };
    }

    return {
      similiarRoles: [],
      similiarSectors: [],
      similiarOrganisations: [],
      similiarCustom: undefined,
    };
  });

  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnDescription, setNewColumnDescription] = useState('');
  const [isAddingItem, setIsAddingItem] = useState<{ [key in keyof ClientProposalRelevantInformation]?: boolean }>({});
  const [selectedItems, setSelectedItems] = useState<{
    [columnId in keyof ClientProposalRelevantInformation]: Record<string, boolean>;
  }>(() => {
    const value: Record<keyof ClientProposalRelevantInformation, Record<string, boolean>> = {
      similiarRoles: {},
      similiarSectors: {},
      similiarOrganisations: {},
      similiarCustom: {},
    };

    if (data) {
      data.similiarRoles?.forEach(item => {
        if (item.id) value.similiarRoles[item.id] = true;
      });

      data.similiarSectors?.forEach(item => {
        if (item.id) value.similiarSectors[item.id] = true;
      });

      data.similiarOrganisations?.forEach(item => {
        if (item.id) value.similiarOrganisations[item.id] = true;
      });

      if (data.similiarCustom?.similiar) {
        data.similiarCustom.similiar.forEach(item => {
          if (item.id) value.similiarCustom[item.id] = true;
        });
      }
    }

    return value;
  });

  const [isEditingColumn, setIsEditingColumn] = useState(false);
  const [editColumnTitle, setEditColumnTitle] = useState('');
  const [editColumnDescription, setEditColumnDescription] = useState('');

  const [rolesMasterList, setRolesMasterList] = useState<SearchResult[]>([]);

  const calculateSimilarity = (str1: string, str2: string): number => {
    if (!str1 || !str2) return 0;

    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    if (s1 === s2) return 1;
    if (s1.includes(` ${s2} `) || s1.startsWith(`${s2} `) || s1.endsWith(` ${s2}`)) return 0.8;
    if (s1.includes(s2)) return 0.6;
    const words = s2.split(' ');
    if (words.some(word => s1.includes(word))) return 0.4;

    return 0;
  };

  const searchRoles = (searchText: string): SearchResult[] => {
    if (!searchText || searchText.length < 2) return [];

    return rolesMasterList
      .map(item => ({
        ...item,
        similarity: Math.max(calculateSimilarity(item.Job, searchText), calculateSimilarity(item.Company, searchText)),
      }))
      .filter(item => item.similarity > 0)
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5)
      .map(({ Job, Company, PartitionKey, RowKey }) => ({
        Job,
        Company,
        PartitionKey,
        RowKey,
      }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (data?.similiarRoles?.length || data?.similiarSectors?.length || data?.similiarOrganisations?.length) {
          return;
        }

        const response = await fetch(`${appConfig.api.baseUrl}/api/rolesmasterlist`);
        const masterListData = await response.json();
        setRolesMasterList(masterListData);

        if (masterListData?.details?.step3Data) {
          const step3Data = masterListData.details.step3Data;

          setColumns(prevColumns => ({
            similiarRoles: step3Data.similiarRoles || [],
            similiarSectors: step3Data.similiarSectors || [],
            similiarOrganisations: step3Data.similiarOrganisations || [],
            similiarCustom: step3Data.similiarCustom || prevColumns.similiarCustom,
          }));

          const newSelectedItems: Record<keyof ClientProposalRelevantInformation, Record<string, boolean>> = {
            similiarRoles: {},
            similiarSectors: {},
            similiarOrganisations: {},
            similiarCustom: {},
          };

          step3Data.similiarRoles?.forEach((item: SimiliarRole) => {
            if (item.id) newSelectedItems.similiarRoles[item.id] = true;
          });

          step3Data.similiarSectors?.forEach((item: SimiliarRole) => {
            if (item.id) newSelectedItems.similiarSectors[item.id] = true;
          });

          step3Data.similiarOrganisations?.forEach((item: SimiliarRole) => {
            if (item.id) newSelectedItems.similiarOrganisations[item.id] = true;
          });

          if (step3Data.similiarCustom?.similiar) {
            step3Data.similiarCustom.similiar.forEach((item: SimiliarRole) => {
              if (item.id) newSelectedItems.similiarCustom[item.id] = true;
            });
          }

          setSelectedItems(newSelectedItems);
          return;
        }

        const limitedData = masterListData.slice(0, 20);

        setColumns(prevColumns => {
          const updateColumn = (existingItems: SimiliarRole[], newItems: SearchResult[]) => {
            const remainingSlots = 20 - existingItems.length;
            if (remainingSlots <= 0) return existingItems;

            const newMappedItems = newItems.slice(0, remainingSlots).map((item: SearchResult) => ({
              id: `${item.Job}-${item.Company}-${Math.random()}`,
              partitionKey: item.PartitionKey,
              rowKey: item.RowKey,
              role: item.Job,
              organization: item.Company,
            }));

            return [...existingItems, ...newMappedItems];
          };

          return {
            similiarRoles: updateColumn(prevColumns.similiarRoles, limitedData),
            similiarSectors: updateColumn(prevColumns.similiarSectors, limitedData.slice(5, 15)),
            similiarOrganisations: updateColumn(prevColumns.similiarOrganisations, limitedData.slice(10, 20)),
            similiarCustom: prevColumns.similiarCustom,
          };
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [data]);

  const handleComplete = () => {
    setIsSubmitting(true);

    const step3Data: ClientProposalRelevantInformation = {
      similiarRoles: columns.similiarRoles.filter(item => selectedItems.similiarRoles[item.id!]),
      similiarSectors: columns.similiarSectors.filter(item => selectedItems.similiarSectors[item.id!]),
      similiarOrganisations: columns.similiarOrganisations.filter(
        item => selectedItems.similiarOrganisations[item.id!],
      ),
      similiarCustom: columns.similiarCustom
        ? {
            ...columns.similiarCustom,
            similiar: columns.similiarCustom.similiar,
          }
        : undefined,
    };

    uploadClientProposalStep3(
      {
        step3Data,
        filename: data?.filename,
      },
      handleAuthError,
    )
      .then(async response => {
        if (response.error) {
          throw new Error(response.error);
        }
        await onSubmit(step3Data);
        onStepClick(WorkflowStep.Main);
      })
      .catch(error => {
        console.error('Error updating metadata:', error);
        handleAuthError();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const moveItem = (
    sourceIndex: number,
    destinationIndex: number,
    columnId: keyof ClientProposalRelevantInformation,
  ) => {
    if (columnId === 'similiarCustom') return;

    const columnItems = [...columns[columnId]];
    const [movedItem] = columnItems.splice(sourceIndex, 1);
    columnItems.splice(destinationIndex, 0, movedItem);

    setColumns({
      ...columns,
      [columnId]: columnItems,
    });
  };

  const addItem = (columnId: keyof ClientProposalRelevantInformation, organisation: string, role: string) => {
    const newItem: SimiliarRole = {
      id: `${role}-${organisation}-${Math.random()}`,
      role,
      organization: organisation,
      partitionKey: '',
      rowKey: '',
    };

    if (columnId === 'similiarCustom' && columns.similiarCustom) {
      if (columns.similiarCustom.similiar.length >= CUSTOM_COLUMN_LIMIT) {
        return;
      }

      setColumns({
        ...columns,
        similiarCustom: {
          ...columns.similiarCustom,
          similiar: [newItem, ...(columns.similiarCustom.similiar || [])],
        },
      });
    } else if (columnId !== 'similiarCustom') {
      setColumns({
        ...columns,
        [columnId]: [newItem, ...columns[columnId]],
      });

      setSelectedItems({
        ...selectedItems,
        [columnId]: {
          ...selectedItems[columnId],
          [newItem.id!]: true,
        },
      });
    }

    setIsAddingItem({ ...isAddingItem, [columnId]: false });
  };

  const addColumn = () => {
    const customColumn = {
      name: newColumnTitle,
      description: newColumnDescription,
      similiar: [] as SimiliarRole[],
    };

    setColumns({
      ...columns,
      similiarCustom: customColumn,
    });

    setIsAddingColumn(false);
    setNewColumnTitle('');
    setNewColumnDescription('');
  };

  const removeColumn = () => {
    const updatedColumns = { ...columns };
    updatedColumns.similiarCustom = undefined;
    setColumns(updatedColumns);
  };

  const toggleSelectItem = (columnId: keyof ClientProposalRelevantInformation, itemId: string) => {
    if (columnId === 'similiarCustom') return;

    setSelectedItems({
      ...selectedItems,
      [columnId]: {
        ...selectedItems[columnId],
        [itemId]: !selectedItems[columnId][itemId],
      },
    });
  };

  const getColumnSelectedItemCount = (columnId: keyof ClientProposalRelevantInformation) => {
    if (columnId === 'similiarCustom') return 0;
    return Object.values(selectedItems[columnId]).filter(Boolean).length;
  };

  const expectedColumnSelectedItemCount = useMemo(() => {
    return 10;
  }, []);
  const isColumnValid = (columnId: keyof ClientProposalRelevantInformation) => {
    return expectedColumnSelectedItemCount >= getColumnSelectedItemCount(columnId);
  };

  const canContinue = useMemo(() => {
    const allColumnsMeetCriteria = Object.values(selectedItems).every(column => {
      const totalSelectedItemsInColumn = Object.values(column).filter(Boolean).length;
      return expectedColumnSelectedItemCount >= totalSelectedItemsInColumn;
    });

    return allColumnsMeetCriteria;
  }, [selectedItems, expectedColumnSelectedItemCount]);

  const columnTitles: Record<keyof Omit<ClientProposalRelevantInformation, 'similiarCustom'>, string> = {
    similiarRoles: 'Similar Roles',
    similiarSectors: 'Similar Sectors',
    similiarOrganisations: 'Similar Organisations',
  };

  const columnDescriptions: Record<keyof Omit<ClientProposalRelevantInformation, 'similiarCustom'>, string> = {
    similiarRoles: 'Roles that are similar to the current position',
    similiarSectors: 'Roles from similar sectors',
    similiarOrganisations: 'Roles from similar organisations',
  };

  const editColumn = () => {
    if (!columns.similiarCustom) return;

    setColumns({
      ...columns,
      similiarCustom: {
        ...columns.similiarCustom,
        name: editColumnTitle,
        description: editColumnDescription,
        similiar: columns.similiarCustom.similiar,
      },
    });

    setIsEditingColumn(false);
  };

  const CUSTOM_COLUMN_LIMIT = 7;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState<EditModalData | null>(null);

  const handleEditClick = (item: SimiliarRole) => {
    setEditModalData({
      rowKey: item.rowKey || '',
      partitionKey: item.partitionKey || '',
      Company: item.organization || '',
      Job: item.role || '',
      id: item.id || '',
    });
    setIsEditModalOpen(true);
  };

  const handleEditSave = () => {
    if (!editModalData) return;

    const updatedColumns = { ...columns };
    Object.keys(updatedColumns).forEach(columnId => {
      const key = columnId as keyof ClientProposalRelevantInformation;
      if (key === 'similiarCustom') return;

      const itemIndex = updatedColumns[key].findIndex(item => item.id === editModalData.id);
      if (itemIndex !== -1) {
        const updatedItem: SimiliarRole = {
          ...updatedColumns[key][itemIndex],
          role: editModalData.Job,
          organization: editModalData.Company,
          partitionKey: editModalData.partitionKey,
          rowKey: editModalData.rowKey,
          id: editModalData.id,
        };

        updatedColumns[key][itemIndex] = updatedItem;

        const updatedSimilarRole = {
          Company: updatedItem.organization,
          Job: updatedItem.role,
          PartitionKey: updatedItem.partitionKey,
          RowKey: updatedItem.rowKey,
        };

        updateSimilarRole(updatedSimilarRole, handleAuthError).then(() => {
          setColumns(updatedColumns);
          setIsEditModalOpen(false);
          setEditModalData(null);
          updateColumn(key, updatedItem);
        });
      }
    });
  };

  const updateColumn = (columnId: keyof ClientProposalRelevantInformation, updatedItem: any) => {
    setColumns(prevColumns => {
      const updatedColumns = { ...prevColumns };
      if (columnId === 'similiarCustom') return updatedColumns;

      const columnItems = [...updatedColumns[columnId]];
      const itemIndex = columnItems.findIndex(item => item.id === updatedItem.Id);

      if (itemIndex !== -1) {
        columnItems[itemIndex] = {
          ...columnItems[itemIndex],
          role: updatedItem.Job,
          organization: updatedItem.Company,
          partitionKey: updatedItem.PartitionKey,
          rowKey: updatedItem.RowKey,
        };

        updatedColumns[columnId] = columnItems;
      }

      return updatedColumns;
    });
  };

  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const findMatchingRoles = async () => {
    try {
      setIsLoadingMatches(true);

      const requestData: FetchSimilarRolesRequest = {
        organization: step2Data?.organisation || data.organisation,
        roleTitle: step2Data?.roleTitle || data.roleTitle,
        roleType: step2Data?.roleType || data.roleType,
        sector: step2Data?.sector || data.sector,
        consultants: [],
      };

      if (!requestData.organization || !requestData.roleTitle || !requestData.roleType) {
        const missingFields = [];
        if (!requestData.organization) missingFields.push('organization');
        if (!requestData.roleTitle) missingFields.push('role title');
        if (!requestData.roleType) missingFields.push('role type');
        if (!requestData.sector) missingFields.push('sector type');

        console.error('Missing required fields:', missingFields);
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const rolesResponse = await fetchSimilarRoles(requestData, handleAuthError);

      if (rolesResponse.error || !rolesResponse.data) {
        console.error('Error response from API:', rolesResponse.error);
        throw new Error(rolesResponse.error || 'Failed to fetch similar roles');
      }

      if (
        !rolesResponse.data.data?.similarRoles ||
        !rolesResponse.data.data?.similarSectors ||
        !rolesResponse.data.data?.similarOrganisations
      ) {
        console.error('Invalid response data structure:', rolesResponse.data);
        throw new Error('Invalid response data structure');
      }

      const mappedRoles = {
        similiarRoles: rolesResponse.data.data.similarRoles.map(role => ({
          id: role.id || `role-${Math.random()}`,
          role: role.role || '',
          organization: role.organization || '',
          partitionKey: role.partitionKey || '',
          rowKey: role.id || '',
        })),
        similiarSectors: rolesResponse.data.data.similarSectors.map(role => ({
          id: role.id || `sector-${Math.random()}`,
          role: role.role || '',
          organization: role.organization || '',
          partitionKey: role.partitionKey || '',
          rowKey: role.id || '',
        })),
        similiarOrganisations: rolesResponse.data.data.similarOrganisations.map(role => ({
          id: role.id || `org-${Math.random()}`,
          role: role.role || '',
          organization: role.organization || '',
          partitionKey: role.partitionKey || '',
          rowKey: role.id || '',
        })),
      };

      setColumns(prevColumns => ({
        ...prevColumns,
        ...mappedRoles,
      }));

      const newSelectedItems: Record<keyof ClientProposalRelevantInformation, Record<string, boolean>> = {
        similiarRoles: {},
        similiarSectors: {},
        similiarOrganisations: {},
        similiarCustom: {},
      };

      mappedRoles.similiarRoles?.forEach(role => {
        if (role.id) newSelectedItems.similiarRoles[role.id] = true;
      });

      mappedRoles.similiarSectors?.forEach(role => {
        if (role.id) newSelectedItems.similiarSectors[role.id] = true;
      });

      mappedRoles.similiarOrganisations?.forEach(role => {
        if (role.id) newSelectedItems.similiarOrganisations[role.id] = true;
      });

      setSelectedItems(newSelectedItems);
    } catch (error) {
      console.error('Error finding matching roles:', error);
      // Ensure error isn't 500
      if (typeof error === 'string' && error.includes('Server error')) {
        setErrorMessage(intl.formatMessage({ id: 'clientProposal.step3.error.serverError' }));
      } else {
        setErrorMessage(intl.formatMessage({ id: 'clientProposal.step3.error.findingMatches' }));
      }
    } finally {
      setIsLoadingMatches(false);
    }
  };

  return (
    <div className={styles.root}>
      <StepHeader
        onBack={onBack}
        onNext={handleComplete}
        currentStep={WorkflowStep.Step3}
        canContinue={canContinue}
        onStepClick={onStepClick}
      />
      <DndProvider backend={HTML5Backend}>
        <h3>Select no more than {expectedColumnSelectedItemCount} items from each column</h3>
        {errorMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setErrorMessage(null)}
            className={styles.errorMessage}
          >
            {errorMessage}
          </MessageBar>
        )}
        <div className={styles.actionBar}>
          <PrimaryButton onClick={findMatchingRoles} disabled={isLoadingMatches} className={styles.findMatchesButton}>
            {intl.formatMessage({ id: 'clientProposal.step3.findMatches' })}
          </PrimaryButton>
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.container}>
            {(Object.keys(columnTitles) as Array<keyof typeof columnTitles>).map(columnId => (
              <div key={columnId} className={styles.column}>
                <h3>
                  {columnTitles[columnId]}{' '}
                  <span
                    className={clsx(styles.columnCount, {
                      [styles.invalid]: !isColumnValid(columnId),
                    })}
                  >
                    ({getColumnSelectedItemCount(columnId)}/{expectedColumnSelectedItemCount})
                  </span>
                </h3>
                <p>{columnDescriptions[columnId]}</p>
                <Divider className={styles.divider} />

                {isAddingItem[columnId] ? (
                  <NewRoleCard
                    onSubmit={(organisation: string, role: string) => addItem(columnId, organisation, role)}
                    onCancel={() => setIsAddingItem({ ...isAddingItem, [columnId]: false })}
                    onSearch={searchRoles}
                  />
                ) : (
                  <DefaultButton
                    className={styles.addButton}
                    onClick={() => setIsAddingItem({ ...isAddingItem, [columnId]: true })}
                  >
                    <AddRegular />
                    Add Card
                  </DefaultButton>
                )}

                {columns[columnId].map((item, index) => (
                  <React.Fragment key={item.id}>
                    <DraggableItem
                      item={item}
                      index={index}
                      columnId={columnId}
                      moveItem={moveItem}
                      isSelected={!!selectedItems[columnId][item.id!]}
                      onSelect={() => toggleSelectItem(columnId, item.id!)}
                      onEdit={handleEditClick}
                    />
                    {index < columns[columnId].length - 1 && <Divider className={styles.divider} />}
                  </React.Fragment>
                ))}
              </div>
            ))}

            {columns.similiarCustom && (
              <div className={styles.column}>
                {isEditingColumn ? (
                  <div className={styles.columnContent}>
                    <TextField
                      label={intl.formatMessage({ id: 'clientProposal.step3.customColumn.nameLabel' })}
                      value={editColumnTitle}
                      onChange={(e, newValue) => setEditColumnTitle(newValue || '')}
                      required
                    />
                    <TextField
                      label={intl.formatMessage({ id: 'clientProposal.step3.customColumn.descriptionLabel' })}
                      value={editColumnDescription}
                      onChange={(e, newValue) => setEditColumnDescription(newValue || '')}
                      multiline
                      rows={3}
                      required
                    />
                    <div className={styles.buttonContainer}>
                      <DefaultButton onClick={() => setIsEditingColumn(false)} className={styles.cancelButton}>
                        {intl.formatMessage({ id: 'clientProposal.step3.customColumn.cancelButton' })}
                      </DefaultButton>
                      <DefaultButton onClick={editColumn} className={styles.addButton}>
                        {intl.formatMessage({ id: 'clientProposal.step3.customColumn.saveButton' })}
                      </DefaultButton>
                    </div>
                  </div>
                ) : (
                  <div className={styles.columnHeader}>
                    <div>
                      <h3>
                        {columns.similiarCustom.name}{' '}
                        <span
                          className={clsx(styles.columnCount, {
                            [styles.invalid]: columns.similiarCustom.similiar.length > CUSTOM_COLUMN_LIMIT,
                          })}
                        >
                          ({columns.similiarCustom.similiar.length}/{CUSTOM_COLUMN_LIMIT})
                        </span>
                      </h3>
                      <p className={styles.description}>{columns.similiarCustom.description}</p>
                    </div>
                    <div className={styles.buttonGroup}>
                      <IconButton
                        title={intl.formatMessage({ id: 'clientProposal.step3.customColumn.editTitle' })}
                        iconProps={{ iconName: 'Edit' }}
                        onClick={() => {
                          setEditColumnTitle(columns.similiarCustom!.name);
                          setEditColumnDescription(columns.similiarCustom!.description);
                          setIsEditingColumn(true);
                        }}
                        className={styles.editButton}
                      />
                      <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={removeColumn}
                        className={styles.removeButton}
                      />
                    </div>
                  </div>
                )}

                <Divider className={styles.divider} />

                {columns.similiarCustom.similiar.length < CUSTOM_COLUMN_LIMIT &&
                  (isAddingItem['similiarCustom'] ? (
                    <NewRoleCard
                      onSubmit={(organisation: string, role: string) => addItem('similiarCustom', organisation, role)}
                      onCancel={() => setIsAddingItem({ ...isAddingItem, ['similiarCustom']: false })}
                      onSearch={searchRoles}
                    />
                  ) : (
                    <DefaultButton
                      className={styles.addButton}
                      onClick={() => setIsAddingItem({ ...isAddingItem, ['similiarCustom']: true })}
                    >
                      <AddRegular />
                      {intl.formatMessage({ id: 'clientProposal.step3.customColumn.addCard' })}
                    </DefaultButton>
                  ))}

                {columns.similiarCustom.similiar &&
                  columns.similiarCustom.similiar.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <DraggableItem
                        item={{
                          id: item.id || '',
                          role: item.role,
                          organization: item.organization,
                          partitionKey: item.partitionKey || '',
                          rowKey: item.rowKey || '',
                        }}
                        index={index}
                        columnId="similiarCustom"
                        moveItem={moveItem}
                        isSelected={!!selectedItems.similiarCustom[item.id || '']}
                        onSelect={() => toggleSelectItem('similiarCustom', item.id || '')}
                        onEdit={handleEditClick}
                      />
                      {index < columns.similiarCustom!.similiar.length - 1 && <Divider className={styles.divider} />}
                    </React.Fragment>
                  ))}
              </div>
            )}

            {!columns.similiarCustom && (
              <div className={`${styles.addColumnContainer} ${isAddingColumn ? styles.expanded : ''}`}>
                {isAddingColumn ? (
                  <div className={styles.columnContent}>
                    <TextField
                      label={intl.formatMessage({ id: 'clientProposal.step3.customColumn.nameLabel' })}
                      value={newColumnTitle}
                      onChange={(e, newValue) => setNewColumnTitle(newValue || '')}
                      required
                    />
                    <TextField
                      label={intl.formatMessage({ id: 'clientProposal.step3.customColumn.descriptionLabel' })}
                      value={newColumnDescription}
                      onChange={(e, newValue) => setNewColumnDescription(newValue || '')}
                      multiline
                      rows={3}
                      required
                    />
                    <div className={styles.buttonContainer}>
                      <DefaultButton onClick={() => setIsAddingColumn(false)} className={styles.cancelButton}>
                        {intl.formatMessage({ id: 'clientProposal.step3.customColumn.cancelButton' })}
                      </DefaultButton>
                      <DefaultButton onClick={addColumn} className={styles.addButton}>
                        <AddRegular />
                        {intl.formatMessage({ id: 'clientProposal.step3.customColumn.addButton' })}
                      </DefaultButton>
                    </div>
                  </div>
                ) : (
                  <IconButton
                    title={intl.formatMessage({ id: 'clientProposal.step3.customColumn.addButton' })}
                    iconProps={{ iconName: 'Add' }}
                    onClick={() => setIsAddingColumn(true)}
                    className={styles.addButton}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </DndProvider>

      <Modal
        isOpen={isEditModalOpen}
        onDismiss={() => {
          setIsEditModalOpen(false);
          setEditModalData(null);
        }}
        isBlocking={true}
      >
        <div className={styles.editModal}>
          <div className={styles.modalHeader}>
            <h2>{intl.formatMessage({ id: 'clientProposal.step3.editModal.title' })}</h2>
            <IconButton
              iconProps={{ iconName: 'Cancel' }}
              onClick={() => {
                setIsEditModalOpen(false);
                setEditModalData(null);
              }}
            />
          </div>
          {editModalData && (
            <div className={styles.modalContent}>
              {[
                { field: 'Job', label: 'Job Title' },
                { field: 'Company', label: 'Company' },
              ].map(({ field, label }) => (
                <TextField
                  key={field}
                  label={label}
                  value={editModalData[field as keyof EditModalData] || ''}
                  onChange={(_, newValue) =>
                    setEditModalData(prev =>
                      prev
                        ? {
                            ...prev,
                            [field]: newValue || '',
                          }
                        : null,
                    )
                  }
                />
              ))}
            </div>
          )}
          <div className={styles.modalFooter}>
            <DefaultButton
              onClick={() => {
                setIsEditModalOpen(false);
                setEditModalData(null);
              }}
              text={intl.formatMessage({ id: 'clientProposal.step3.editModal.cancel' })}
            />
            <PrimaryButton
              onClick={handleEditSave}
              text={intl.formatMessage({ id: 'clientProposal.step3.editModal.save' })}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isSubmitting}
        isBlocking={true}
        styles={{
          main: styles.submittingModal,
        }}
      >
        <div className={styles.spinnerContainer}>
          <Spinner size={3} />
          <div>{intl.formatMessage({ id: 'clientProposal.step3.submitting' })}</div>
        </div>
      </Modal>

      {isLoadingMatches && (
        <Overlay className={styles.fullScreenOverlay}>
          <div className={styles.loadingContent}>
            <Spinner size={3} />
            <MessageBar messageBarType={MessageBarType.info}>
              {intl.formatMessage({ id: 'clientProposal.step3.findingMatches' })}
            </MessageBar>
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default Step3;
